import React from "react";
import { motion } from "framer-motion";
import { FaExternalLinkAlt } from "react-icons/fa";

const About = () => {
  const educations = [
    {
      school: "Hacktiv8 Indonesia",
      period: "Feb 2024 - Jun 2024",
      program: "Full Stack Javascript Immersive Program",
      links: [
        { label: "Transcript", url: "https://drive.google.com/file/d/18QotHjVMZnzdAcXN_wz3ujEPlZzntWvK/view?usp=sharing" },
        { label: "Certificate", url: "https://drive.google.com/file/d/1f1aOQkbxCRKqDEVhiYE9DfSDUxNQHs1e/view?usp=sharing" },
      ],
    },
    {
      school: "Algoritma Data Science School",
      period: "Jul 2020 - Oct 2020",
      program: "Full Stack Data Science Program",
      links: [
        { label: "Certificate", url: "https://drive.google.com/drive/folders/16bgMI9zBJWg---2dV1M_NNu-ZKYVti7p?usp=sharing" },
      ],
    },
    {
      school: "Padjadjaran University",
      period: "Jun 2015 - Dec 2019",
      program: "Bachelor of Geophysics",
      links: [],
    },
  ];

  const experiences = [
    { company: "PT. Bringin Inti Teknologi", role: "Back-End Developer", period: "Mar 2025 - Present", current: true },
    { company: "PT. Bringin Inti Teknologi", role: "Full-Stack Developer Intern", period: "Sep 2024 - Mar 2025" },
    { company: "FIFGROUP (ASTRA)", role: "Area Marketing Data Analyst", period: "Aug 2021 - Jan 2024" },
    { company: "FIFGROUP (ASTRA)", role: "Management Development Program", period: "Sep 2020 - Aug 2021" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -15 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
  };

  return (
    <section className="relative overflow-hidden py-16 sm:py-24">
      <div className="absolute inset-0 bg-dark-900" />
      <div className="absolute inset-0 grid-overlay" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <motion.div
          className="mb-10 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="font-mono text-xs text-gray-400 block mb-3">
            {"// "}section.about
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
            About <span className="text-glow">Me</span>
          </h2>
          <div className="section-divider mt-4 max-w-xs" />
        </motion.div>

        {/* Profile + Bio */}
        <div className="flex flex-col lg:flex-row gap-8 sm:gap-12 mb-14 sm:mb-20">
          <motion.div
            className="lg:w-2/5"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative group">
              <div className="absolute -inset-px bg-sky-400/10 group-hover:bg-sky-400/20 transition-all duration-500" />
              <img
                src="Petrus_Arditama.jpg"
                alt="Petrus Arditama"
                className="relative w-full h-[320px] sm:h-[400px] lg:h-[480px] object-cover grayscale-[20%] hover:grayscale-0 transition-all duration-500"
                style={{ objectPosition: "center top" }}
              />
              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-sky-400/50" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-sky-400/50" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-sky-400/50" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-sky-400/50" />
            </div>
          </motion.div>

          <motion.div
            className="lg:w-3/5"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <p className="text-gray-200 text-sm sm:text-base leading-relaxed mb-4 sm:mb-5">
              Hi, I'm <span className="text-white font-semibold">Petrus Arditama</span>, a passionate
              Full-Stack Developer with expertise in creating dynamic and
              responsive web applications. Transitioned from a successful career
              in finance to embrace a lifelong dream of programming.
            </p>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-6 sm:mb-8">
              Completed an intensive Hacktiv8 bootcamp, emerging as a skilled
              full-stack developer. I have a strong background in JavaScript,
              React, Node.js, Go, and more. My goal is to build scalable and
              efficient applications that provide excellent user experiences.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {[
                { num: "4+", label: "Projects" },
                { num: "2+", label: "Yrs in Tech" },
                { num: "5+", label: "Yrs Working" },
              ].map((s) => (
                <div key={s.label} className="glass-card p-3 sm:p-4 text-center">
                  <div className="font-mono text-lg sm:text-xl font-bold text-sky-400">{s.num}</div>
                  <div className="text-[10px] sm:text-xs text-gray-400 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Education & Experience */}
        <div className="grid lg:grid-cols-2 gap-10 sm:gap-12">
          {/* Education */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <span className="font-mono text-xs text-sky-400">{">"}</span>
              <h3 className="text-lg sm:text-xl font-bold text-white">education</h3>
              <div className="flex-1 h-px bg-sky-400/15" />
            </div>

            <div className="space-y-1">
              {educations.map((edu, i) => (
                <motion.div key={i} variants={itemVariants} className="timeline-line pb-6 sm:pb-7 last:pb-0">
                  <div className="timeline-dot" />
                  <div className="glass-card p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-3 mb-2">
                      <h4 className="text-white text-xs sm:text-sm font-medium">{edu.school}</h4>
                      <span className="font-mono text-[10px] text-sky-300 whitespace-nowrap">{edu.period}</span>
                    </div>
                    <p className="text-gray-300 text-xs">{edu.program}</p>
                    {edu.links.length > 0 && (
                      <div className="flex gap-3 mt-3">
                        {edu.links.map((link) => (
                          <a
                            key={link.label}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 font-mono text-[10px] text-sky-400 hover:text-sky-300 transition-colors"
                          >
                            <FaExternalLinkAlt className="text-[8px]" />
                            {link.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Experience */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <span className="font-mono text-xs text-sky-400">{">"}</span>
              <h3 className="text-lg sm:text-xl font-bold text-white">experience</h3>
              <div className="flex-1 h-px bg-sky-400/15" />
            </div>

            <div className="space-y-1">
              {experiences.map((exp, i) => (
                <motion.div key={i} variants={itemVariants} className="timeline-line pb-6 sm:pb-7 last:pb-0">
                  <div className="timeline-dot" />
                  <div className="glass-card p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-2 sm:gap-3 mb-2">
                      <h4 className="text-white text-xs sm:text-sm font-medium">{exp.role}</h4>
                      {exp.current && (
                        <span className="font-mono text-[10px] text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 shrink-0">
                          ACTIVE
                        </span>
                      )}
                    </div>
                    <p className="text-gray-300 text-xs">{exp.company}</p>
                    <p className="font-mono text-[10px] text-gray-400 mt-1">{exp.period}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
